import { getBrowserTimeZone, getZonedCalendarDate } from 'helpers/dateTimeHelper';
import { MAX_DAY_OF_MONTH, LAST_DAY_OF_MONTH } from 'helpers/recurrenceLimits';

// Date.getDay() is 0 (Sunday) through 6 (Saturday).
var DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// Anchor date for backfilling an empty slot array: the calendar day the rule's
// start date falls on in the RULE's zone (the engine derives the weekday and
// day-of-month from the same instant in that zone), or today when there is
// none yet. Read browser-locally, a Tokyo Monday-midnight start shows as
// Sunday from Los Angeles and the next save would persist Sunday.
function scheduleAnchorDate(startDate, timeZone) {
  return startDate ? getZonedCalendarDate(startDate, timeZone) : new Date();
}

// Flatten nested settings-fields tree (fields may have `children`) into a flat
// list keyed by suuid so title lookups are O(1).
export function buildFieldIndex(settingsFields) {
  var index = {};
  if (!settingsFields) return index;
  var walk = function (list) {
    for (var i = 0; i < list.length; i++) {
      var f = list[i];
      if (f && f.suuid) index[f.suuid] = f;
      if (f && f.children) walk(f.children);
    }
  };
  walk(settingsFields);
  return index;
}

function titleFor(index, suuid) {
  if (!suuid) return '';
  var f = index[suuid];
  return (f && f.title) || '';
}

function mapRecurrenceType(apiType) {
  if (apiType === 'scheduled') return 'schedule';
  if (apiType === 'manual') return 'manual';
  return 'none';
}

function adoptCustomColumns(list) {
  if (!Array.isArray(list)) return [];
  return list.filter(function (c) { return c && typeof c === 'object'; }).map(function (c) {
    return {
      source: c.source,
      fieldSUUID: c.fieldSUUID,
      fieldAttribute: c.fieldAttribute,
      afterColumn: c.afterColumn,
      fieldPath: Array.isArray(c.fieldPath) ? c.fieldPath.slice() : [],
      fieldTitle: c.fieldTitle || '',
      fieldType: c.fieldType || '',
      isOrphaned: !!c.isOrphaned,
    };
  });
}

export function adoptInspectionResource(resource, settingsFields) {
  var r = resource || {};
  var index = buildFieldIndex(settingsFields);

  var recurrenceMode = mapRecurrenceType(r.recurrenceType);
  var recurrence = { mode: recurrenceMode };
  if (recurrenceMode === 'manual') {
    recurrence.fieldKey = r.manualRecurrenceDateFieldPathHash || null;
    recurrence.fieldTitle = titleFor(index, recurrence.fieldKey);
  } else if (recurrenceMode === 'schedule') {
    recurrence.frequency = r.scheduledRecurrenceFrequency || 'daily';
    recurrence.interval = r.scheduledRecurrenceInterval || 1;
    recurrence.daysOfWeek = (r.scheduledRecurrenceDaysOfWeek && r.scheduledRecurrenceDaysOfWeek.slice()) || [];
    recurrence.hoursOfDay = (r.scheduledRecurrenceHoursOfDay && r.scheduledRecurrenceHoursOfDay.slice()) || [];
    recurrence.daysOfMonth = (r.scheduledRecurrenceDaysOfMonth && r.scheduledRecurrenceDaysOfMonth.slice()) || [];
    recurrence.startDate = r.scheduledRecurrenceStartDate || null;
    // The API's response serializer uses NullValueHandling.Ignore, so a null
    // timeZone is dropped from the JSON entirely rather than sent as
    // "timeZone": null -- a missing key and an explicit null must fall back
    // identically, and this falsy check treats both (and '') the same way
    // without asserting on the key's presence.
    recurrence.timeZone = r.scheduledRecurrenceTimeZone || getBrowserTimeZone();
    // A weekly/monthly rule with no slots yet (e.g. migrated from the old
    // interval-only fields) recurs on the day its start date already falls on,
    // so the cadence editor never opens to an empty, invalid-looking selection.
    if (recurrence.frequency === 'weekly' && recurrence.daysOfWeek.length === 0) {
      recurrence.daysOfWeek = [DAY_KEYS[scheduleAnchorDate(recurrence.startDate, recurrence.timeZone).getDay()]];
    }
    // Mirrors InspectionService+RecurrenceMigration's LegacyMonthlyDayOfMonth: a day the picker
    // cannot offer (29-31) is Last Day (-1), which never skips a short month.
    if (recurrence.frequency === 'monthly' && recurrence.daysOfMonth.length === 0) {
      var startDay = scheduleAnchorDate(recurrence.startDate, recurrence.timeZone).getDate();
      recurrence.daysOfMonth = [startDay > MAX_DAY_OF_MONTH ? LAST_DAY_OF_MONTH : startDay];
    }
    // The API requires exactly one day (ruling 2026-09-15) and the picker is a single
    // select showing the first entry, so a multi-day rule stored before that rule
    // keeps only what the picker shows; otherwise an untouched save would be rejected
    // under a select that visibly holds a legal value.
    if (recurrence.frequency === 'monthly' && recurrence.daysOfMonth.length > 1) {
      recurrence.daysOfMonth = recurrence.daysOfMonth.slice(0, 1);
    }
    recurrence.firstDueAnchor = r.scheduledFirstDueAnchor || 'today';
    recurrence.firstDueDate = r.scheduledFirstDueDate || null;
    recurrence.firstDueFieldKey = r.scheduledFirstDueFieldPathHash || null;
    recurrence.firstDueFieldTitle = titleFor(index, recurrence.firstDueFieldKey);
    recurrence.firstDueOffsetInterval =
      r.scheduledFirstDueOffsetInterval === null || r.scheduledFirstDueOffsetInterval === undefined
        ? null
        : r.scheduledFirstDueOffsetInterval;
    recurrence.firstDueOffsetUnit = r.scheduledFirstDueOffsetUnit || 'days';
    // A value already on the resource came from a prior deliberate save (the
    // user's own edit, or a server-side seed the user has since seen), so it
    // is adopted as custom -- EarlySubmissionEditor must not silently overwrite it
    // on the next cadence change. Only a genuinely unconfigured grace (null,
    // never undefined-vs-zero confusion: 0 is a real, deliberate value) comes
    // back not-custom, so the editor seeds a fresh default itself.
    recurrence.graceInterval =
      r.scheduledCompletionGraceInterval === null || r.scheduledCompletionGraceInterval === undefined
        ? null
        : r.scheduledCompletionGraceInterval;
    recurrence.graceUnit = r.scheduledCompletionGraceUnit || null;
    recurrence.graceIsCustom = recurrence.graceInterval !== null;
  }

  var neglectWarnings = null;
  if (r.neglectWarningsEnabled) {
    neglectWarnings = {
      every: r.neglectWarningsPeriodInterval,
      unit: r.neglectWarningsPeriodUnit || '',
    };
  }

  var passFailRules = r.passFailRules || {};
  var passFailCriteria = {
    mode: r.passOrFail || null,
    andOr: passFailRules.andOr || 'and',
    rules: (passFailRules.rules && passFailRules.rules.slice()) || [],
  };

  var assetTypeFieldKey = r.assetTypeFieldPathHash || null;
  var activeFieldKey = r.isActiveFieldPathHash || null;

  return {
    recordsFieldKey: r.inspectionRecordsFieldPathHash || '',
    recordsFieldTitle: r.inspectionRecordsFieldTitle || '',
    passFailCriteria: passFailCriteria,
    assetTypeFieldKey: assetTypeFieldKey,
    assetTypeFieldTitle: titleFor(index, assetTypeFieldKey),
    assetTypeValue: r.assetTypeValue || '',
    recurrence: recurrence,
    activeFieldKey: activeFieldKey,
    activeFieldTitle: titleFor(index, activeFieldKey),
    neglectWarnings: neglectWarnings,
    historyCustomColumns: adoptCustomColumns(r.historyCustomColumns),
    scheduledCustomColumns: adoptCustomColumns(r.scheduledCustomColumns),
    notifications: { onPass: [], onFail: [], scheduledBefore: [], scheduledAfter: [], onNeglect: [], summary: [] },
  };
}
